import { content } from "../../config/content.js";
import { buildAnimatedHeading } from "../HeroTitleAnimation.js";
import { buildSectionPillRow } from "../components/SectionPill.js";

const orbitIcons = ["◆", "●", "▲", "■", "✦"];

export function createNumbersSection() {
  const { numbers } = content;
  const section = document.createElement("section");
  section.id = "numbers";
  section.className = "section numbers";

  section.innerHTML = `
    <div class="section__inner">
      <div class="numbers__intro reveal">
        ${buildSectionPillRow(numbers.badge)}
        <h2 class="numbers__title">${buildAnimatedHeading(numbers.titleSegments)}</h2>
        <p class="numbers__subtitle">${numbers.subtitle}</p>
      </div>

      <div class="numbers__grid">
        <article class="numbers-card numbers-card--stack reveal">
          <div class="numbers-card__visual">
            <div class="numbers__tiles">
              ${numbers.cardStack.tiles
                .map((t, i) => `<span class="numbers__tile numbers__tile--${i}">${t}</span>`)
                .join("")}
            </div>
            <span class="numbers__stars" aria-hidden="true">★★★★★</span>
          </div>
          <span class="numbers-card__value">${numbers.cardStack.value}</span>
          <h3>${numbers.cardStack.title}</h3>
          <p>${numbers.cardStack.description}</p>
        </article>

        <div class="numbers__middle">
          <article class="numbers-card numbers-card--orbit reveal" style="--delay: 0.1s">
            <div class="numbers__orbit" aria-hidden="true">
              <span class="numbers__orbit-arc"></span>
              <span class="numbers__orbit-core">T</span>
              ${orbitIcons
                .map((icon, i) => `<span class="numbers__orbit-node numbers__orbit-node--${i}">${icon}</span>`)
                .join("")}
            </div>
          </article>
          <article class="numbers-card reveal" style="--delay: 0.2s">
            <span class="numbers-card__value">${numbers.cardOrbit.value}</span>
            <h3>${numbers.cardOrbit.title}</h3>
            <p>${numbers.cardOrbit.description}</p>
          </article>
        </div>

        <article class="numbers-card numbers-card--rank reveal" style="--delay: 0.3s">
          <span class="numbers-card__value">${numbers.cardRank.value}</span>
          <h3>${numbers.cardRank.title}</h3>
          <p>${numbers.cardRank.description}</p>
          <div class="numbers__ranks">
            ${numbers.cardRank.rows
              .map(
                (row, i) => `
              <div class="numbers__rank" style="--rank-i: ${i}">
                <span class="numbers__avatar numbers__avatar--${i}">${row.initials}</span>
                <span class="numbers__rank-label">${row.label}</span>
                ${row.badge ? `<span class="numbers__rank-badge">${row.badge}</span>` : ""}
              </div>
            `
              )
              .join("")}
          </div>
        </article>
      </div>
    </div>
  `;

  observeCountUp(section);
  return section;
}

/* Animates values like "120+", "94,200+", "89%" from 0 to their target
   the first time their card scrolls into view. */
function observeCountUp(section) {
  const easeOut = (t) => 1 - Math.pow(1 - t, 4);

  const animate = (el) => {
    const raw = el.textContent;
    const match = raw.match(/^([\d,]+)(.*)$/);
    if (!match) return;

    const target = parseInt(match[1].replace(/,/g, ""), 10);
    const suffix = match[2];
    const duration = 1600;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const value = Math.round(target * easeOut(progress));
      el.textContent = value.toLocaleString("en-US") + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          // Small delay so the count starts as the card text fades in
          setTimeout(() => animate(entry.target), 400);
          observer.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.4 }
  );

  section.querySelectorAll(".numbers-card__value").forEach((el) => observer.observe(el));
}
